import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './styles/aiTutor.scss';
import { ApiHelper } from '../../apiHelper/ApiHelper';
import { readJson } from '../../apiHelper/readJson';

type Message = { role: number; content: string; image?: string; notice?: boolean; streaming?: boolean };

const SUGGESTIONS = [
    { icon: "🧬", title: "Osmóza a buňka", text: "Vysvětli, co je osmóza a co se děje s buňkou v různých roztocích." },
    { icon: "⚙️", title: "Newtonovy zákony", text: "Jak fungují Newtonovy zákony pohybu a kde je v praxi vidím?" },
    { icon: "⚗️", title: "Chemická vazba", text: "Co je chemická vazba a jaké typy rozlišujeme?" },
];

const TOOLS = [
    { icon: "💡", label: "Vysvětli jednoduše", template: "Vysvětli co nejjednodušeji, jako bych to slyšel poprvé: " },
    { icon: "📝", label: "Shrň látku", template: "Shrň stručně v bodech to nejdůležitější o tématu: " },
    { icon: "🎯", label: "Otestuj mě", template: "Dej mi 3 krátké otázky k procvičení na téma: " },
    { icon: "🔗", label: "Porovnej", template: "Vysvětli rozdíl a porovnej: " },
    { icon: "🧠", label: "Mnemotechnika", template: "Vymysli mnemotechnickou pomůcku pro zapamatování: " },
    { icon: "📐", label: "Příklad", template: "Ukaž konkrétní praktický příklad k: " },
];

type TutorHandoff = { tutorQuestion?: string; tutorContext?: string; tutorSubject?: number };

function BotAvatar() {
    return (
        <div className="avatar">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="7" width="16" height="11" rx="3" />
                <path d="M12 7V4" /><circle cx="12" cy="3" r="1" />
                <circle cx="9" cy="12" r="1" /><circle cx="15" cy="12" r="1" />
                <path d="M9.5 15.5h5" />
            </svg>
        </div>
    );
}

function downscale(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const img = new Image();
            img.onload = () => {
                const max = 1280;
                let { width, height } = img;
                if (Math.max(width, height) > max) {
                    const scale = max / Math.max(width, height);
                    width = Math.round(width * scale);
                    height = Math.round(height * scale);
                }
                const canvas = document.createElement("canvas");
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext("2d");
                if (!ctx) { resolve(reader.result as string); return; }
                ctx.drawImage(img, 0, 0, width, height);
                resolve(canvas.toDataURL("image/jpeg", 0.85));
            };
            img.onerror = reject;
            img.src = reader.result as string;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

export default function AiTutor() {
    const location = useLocation();
    const handoff = (location.state ?? {}) as TutorHandoff;

    const [messages, setMessages] = useState<Message[]>([]);
    const [input, setInput] = useState(handoff.tutorQuestion ?? "");
    const [image, setImage] = useState<string | null>(null);
    const [sending, setSending] = useState(false);
    const [remainingDaily, setRemainingDaily] = useState<number | null>(null);
    const [remainingWeekly, setRemainingWeekly] = useState<number | null>(null);
    const [capMessage, setCapMessage] = useState<string | null>(null);
    const [searchOpen, setSearchOpen] = useState(false);
    const [search, setSearch] = useState("");
    const pendingContext = useRef<string | undefined>(handoff.tutorContext);
    const subject = useRef<number | undefined>(handoff.tutorSubject);
    const fileRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const taRef = useRef<HTMLTextAreaElement>(null);

    const api = new ApiHelper();

    useEffect(() => {
        loadHistory();
        document.body.classList.add("chatFull");
        return () => document.body.classList.remove("chatFull");
    }, []);
    useEffect(() => { if (listRef.current && !searchOpen && messages.length > 0) listRef.current.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" }); }, [messages, sending, searchOpen]);
    useEffect(() => {
        const ta = taRef.current;
        if (!ta) return;
        ta.style.height = "auto";
        ta.style.height = Math.min(ta.scrollHeight, 160) + "px";
    }, [input]);

    const query = search.trim().toLowerCase();
    const shown = useMemo(
        () => query ? messages.filter(m => m.content.toLowerCase().includes(query)) : messages,
        [messages, query]
    );

    async function loadHistory() {
        try {
            const res = await api.aiTutorApi.getHistoryList();
            const data = await readJson(res);
            setMessages((data.messages ?? []).map((m: any) => ({ role: m.role, content: m.content, image: m.image ?? undefined })));
            setRemainingDaily(data.remainingDaily);
            setRemainingWeekly(data.remainingWeekly);
        } catch (x: any) { api.processErrors(x); }
    }

    async function onPickImage(e: React.ChangeEvent<HTMLInputElement>) {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        try { setImage(await downscale(file)); } catch { api.alert.error("Obrázek se nepodařilo načíst"); }
    }

    function applyTool(template: string) {
        if (capMessage) return;
        setInput(prev => template + prev);
        taRef.current?.focus();
    }

    function toggleSearch() {
        setSearchOpen(o => { if (o) setSearch(""); return !o; });
    }

    // Update the last message (the in-progress assistant bubble) in place.
    function patchLast(patch: (m: Message) => Message) {
        setMessages(prev => prev.map((m, i) => (i === prev.length - 1 ? patch(m) : m)));
    }

    async function send(text: string) {
        const question = text.trim();
        const photo = image;
        if ((question.length === 0 && !photo) || sending || capMessage) return;

        const context = pendingContext.current;
        pendingContext.current = undefined;
        setInput("");
        setImage(null);
        if (searchOpen) { setSearchOpen(false); setSearch(""); }
        // Optimistically add the user bubble and an empty assistant bubble that streams in.
        setMessages(prev => [...prev, { role: 0, content: question, image: photo ?? undefined }, { role: 1, content: "", streaming: true }]);
        setSending(true);

        const controller = new AbortController();
        try {
            await api.askTutorStream(
                { message: question, subject: subject.current, context, image: photo ?? undefined },
                {
                    onDelta: token => patchLast(m => ({ ...m, content: m.content + token })),
                    onCap: (msg, d, w) => {
                        setRemainingDaily(d);
                        setRemainingWeekly(w);
                        setCapMessage(msg);
                        patchLast(() => ({ role: 1, content: msg, notice: true }));
                    },
                    onDone: (d, w) => {
                        setRemainingDaily(d);
                        setRemainingWeekly(w);
                        patchLast(m => ({ ...m, streaming: false }));
                    },
                },
                controller.signal
            );
        } catch (x: any) {
            if (controller.signal.aborted) return;
            // Roll back the optimistic user + assistant bubbles and restore the draft.
            setMessages(prev => prev.slice(0, -2));
            setInput(question);
            setImage(photo);
            pendingContext.current = context;
            api.processErrors(x);
        } finally {
            setSending(false);
        }
    }

    function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send(input);
        }
    }

    const empty = messages.length === 0;
    const blocked = capMessage !== null;
    const canSend = !blocked && !sending && (input.trim().length > 0 || image !== null);

    return (
        <div className="aiTutor">
            <div className="chat">
                <div className="chatTop">
                    <div className="who">
                        <BotAvatar />
                        <div className="whoText">
                            <h1>AI tutor <span className="dot" /></h1>
                            <p>Vysvětlí pojmy z učebnic — zeptej se, nebo vyfoť otázku z knihy</p>
                        </div>
                    </div>
                    <div className="topActions">
                        {remainingDaily !== null &&
                            <span className="usage" title="Zbývající otázky"><b>{remainingDaily}</b> dnes · <b>{remainingWeekly}</b> týden</span>
                        }
                        <button className={"iconBtn" + (searchOpen ? " active" : "")} title="Hledat v historii" onClick={toggleSearch} disabled={empty}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="7" /><path d="m20 20-3.2-3.2" />
                            </svg>
                        </button>
                    </div>
                </div>

                {searchOpen &&
                    <div className="searchBar">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="7" /><path d="m20 20-3.2-3.2" />
                        </svg>
                        <input autoFocus placeholder="Hledat ve zprávách…" value={search} onChange={e => setSearch(e.target.value)} />
                        {query && <span className="count">{shown.length}</span>}
                        <span className="clear" onClick={toggleSearch}>×</span>
                    </div>
                }

                <div className="messages" ref={listRef}>
                    {empty &&
                        <div className="welcome">
                            <div className="hero"><BotAvatar /></div>
                            <h2>Ahoj! Jak ti dnes pomůžu?</h2>
                            <p className="lead">Odpovídám na základě nahraných učebnic a vždy uvedu, odkud informace pochází. Můžeš mi i vyfotit otázku z knihy.</p>
                            <div className="suggestions">
                                {SUGGESTIONS.map(s => (
                                    <div key={s.title} className="suggestion" onClick={() => send(s.text)}>
                                        <span className="sIcon">{s.icon}</span>
                                        <span className="sBody">
                                            <span className="sTitle">{s.title}</span>
                                            <span className="sText">{s.text}</span>
                                        </span>
                                        <span className="sArrow">→</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    }

                    {!empty && query && shown.length === 0 &&
                        <div className="noResults">Žádná zpráva neodpovídá hledání „{search.trim()}".</div>
                    }

                    {shown.map((m, i) => (
                        <div key={i} className={"msg " + (m.role === 0 ? "mine" : "tutor") + (m.notice ? " notice" : "")}>
                            {m.role === 1 && <BotAvatar />}
                            <div className="bubbleWrap">
                                <div className="bubble">
                                    {m.image && <img className="msgImage" src={m.image} alt="" />}
                                    {m.role === 1 && m.streaming && m.content.length === 0
                                        ? <div className="typing"><span /><span /><span /></div>
                                        : m.role === 1 && !m.notice
                                            ? <div className="md"><ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown></div>
                                            : <span className="plain">{m.content}</span>}
                                </div>
                            </div>
                        </div>
                    ))}

                </div>

                {blocked && <div className="capNotice">{capMessage}</div>}

                {!blocked && !searchOpen &&
                    <div className="tools">
                        {TOOLS.map(t => (
                            <button key={t.label} className="tool" onClick={() => applyTool(t.template)} title={t.label}>
                                <span className="tIcon">{t.icon}</span>{t.label}
                            </button>
                        ))}
                    </div>
                }

                {image && !blocked &&
                    <div className="attachment">
                        <img src={image} alt="" />
                        <span className="aText">Přiložený obrázek</span>
                        <span className="remove" onClick={() => setImage(null)}>×</span>
                    </div>
                }

                <div className="composer">
                    <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPickImage} />
                    <div className={"attachBtn" + (blocked ? " disabled" : "")} title="Vyfotit / nahrát obrázek" onClick={() => !blocked && fileRef.current?.click()}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15l-5-5L5 21" />
                            <path d="M3 17V5a2 2 0 0 1 2-2h12" />
                            <circle cx="9" cy="9" r="2" />
                        </svg>
                    </div>
                    <textarea
                        ref={taRef}
                        className="input"
                        placeholder={blocked ? "Limit otázek byl vyčerpán" : "Napiš svůj dotaz…"}
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        onKeyDown={onKeyDown}
                        rows={1}
                        disabled={blocked || sending}
                    />
                    <button className={"sendBtn" + (canSend ? "" : " disabled")} onClick={() => send(input)} disabled={!canSend}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M5 12h13" /><path d="M13 6l6 6-6 6" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    );
}
